import { coalesceConsecutiveImageFeedRows } from "./waveImageCoalesce.js";
import { isAgentWidgetRelation } from "./waveWhaleHelpers.js";
function blockTimestamp(block) {
    const iso = block.createdAt ?? block.updatedAt;
    if (!iso)
        return 0;
    const t = new Date(iso).getTime();
    return Number.isNaN(t) ? 0 : t;
}
function isTopLevel(block) {
    return !block.payload.parentId;
}
function isBlockArchived(block) {
    return block.payload.archived === true;
}
/** True when the block is hidden from the active thread feed. */
export function isWaveBlockArchived(block) {
    return isBlockArchived(block);
}
function archivedSortKey(block) {
    const iso = block.payload.archivedAt ?? block.updatedAt ?? block.createdAt;
    if (!iso)
        return 0;
    const t = new Date(iso).getTime();
    return Number.isNaN(t) ? 0 : t;
}
/** Split blocks into active feed vs archived (newest archived first). */
export function partitionArchivedBlocks(blocks) {
    const active = [];
    const archived = [];
    for (const block of blocks) {
        if (isBlockArchived(block))
            archived.push(block);
        else
            active.push(block);
    }
    archived.sort((a, b) => archivedSortKey(b) - archivedSortKey(a));
    return { active, archived };
}
function compareTimelineBlocks(a, b, order) {
    const diff = blockTimestamp(a) - blockTimestamp(b);
    const chronological = diff !== 0 ? diff : a.sortIndex - b.sortIndex;
    return order === "desc" ? -chronological : chronological;
}
/** Chronological order; nested children appear at their own createdAt (flattened). */
export function timelineProjection(blocks, order = "asc") {
    const sorted = [...blocks].sort((a, b) => compareTimelineBlocks(a, b, order));
    return sorted.map((block) => ({
        block,
        depth: block.payload.parentId ? 1 : 0,
        relation: block.payload.relation,
    }));
}
/** Document order: top-level blocks in array order, each followed by its children (depth 1). */
export function storyProjection(blocks) {
    const byId = new Map(blocks.map((b) => [b.id, b]));
    const childrenByParent = new Map();
    for (const block of blocks) {
        const parentId = block.payload.parentId;
        if (!parentId)
            continue;
        const list = childrenByParent.get(parentId) ?? [];
        list.push(block);
        childrenByParent.set(parentId, list);
    }
    const result = [];
    for (const block of blocks) {
        if (!isTopLevel(block))
            continue;
        result.push({ block, depth: 0 });
        const children = childrenByParent.get(block.id) ?? [];
        const sortedChildren = [...children].sort((a, b) => a.sortIndex - b.sortIndex);
        for (const child of sortedChildren) {
            if (!byId.has(child.id))
                continue;
            result.push({
                block: child,
                depth: 1,
                relation: child.payload.relation,
            });
        }
    }
    // Orphan children (parent missing): append at end as top-level
    for (const block of blocks) {
        const parentId = block.payload.parentId;
        if (!parentId)
            continue;
        if (!byId.has(parentId)) {
            result.push({ block, depth: 0 });
        }
    }
    return result;
}
export function projectThreadFeed(blocks, view, order = "asc") {
    const base = view === "timeline" ? timelineProjection(blocks, order) : storyProjection(blocks);
    return coalesceConsecutiveImageFeedRows(base);
}
/** Active (non-archived) nested children per parent block id. */
export function nestedChildCountByParent(blocks) {
    const counts = new Map();
    for (const block of blocks) {
        const parentId = block.payload.parentId;
        if (!parentId || isBlockArchived(block))
            continue;
        counts.set(parentId, (counts.get(parentId) ?? 0) + 1);
    }
    return counts;
}
/** Hide nested rows whose parent id is in `hiddenParentIds` (empty set = no-op). */
export function filterCollapsedNestedRows(rows, hiddenParentIds) {
    if (!hiddenParentIds || hiddenParentIds.size === 0)
        return rows;
    return rows.filter((row) => {
        const parentId = row.block.payload.parentId;
        return !parentId || !hiddenParentIds.has(parentId);
    });
}
/** Whether an active top-level block can move up/down among other active top-level blocks. */
export function topLevelMoveBounds(blocks, blockId) {
    const { active } = partitionArchivedBlocks(blocks);
    const topLevelActive = active.filter(isTopLevel);
    const idx = topLevelActive.findIndex((b) => b.id === blockId);
    if (idx < 0) {
        return { canMoveUp: false, canMoveDown: false };
    }
    return {
        canMoveUp: idx > 0,
        canMoveDown: idx < topLevelActive.length - 1,
    };
}
/** One-line preview for a collapsed nested widget card. */
export function collapsedBlockPreviewLabel(block) {
    if (isAgentWidgetRelation(block.payload.relation)) {
        const question = block.payload.agentQuestion?.trim();
        if (question) {
            const oneLine = question.replace(/\s+/g, " ");
            return oneLine.length > 72 ? `${oneLine.slice(0, 72)}…` : oneLine;
        }
    }
    const title = block.payload.title?.trim();
    if (title)
        return title;
    const text = block.payload.text?.trim();
    if (text) {
        const firstLine = text.split(/\r?\n/)[0]?.trim() ?? text;
        const oneLine = firstLine.replace(/\s+/g, " ");
        return oneLine.length > 72 ? `${oneLine.slice(0, 72)}…` : oneLine;
    }
    return block.payload.fileName?.trim() ?? block.kind;
}
